import { SerializedError } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';

import { ENV_BACKEND_DOMAIN, REQUEST_METHODS } from '@lib/core/service/consts';
import { handleAxiosErrorScenarios } from '@lib/core/service/requests/handleAxiosErrorScenarios';
import { parseError } from '@lib/core/service/requests/parseError';
import { selectLocale } from '@lib/core/service/selectors';
import { store } from '@lib/core/service/store';
import { selectAuthToken } from '@lib/core/users/selectors/auth';
import { selectDevtoolsUserAuthToken, selectDevtoolsUserProfileId } from '@lib/core/users/selectors/devtoolsUser';
import { selectProfileId } from '@lib/core/users/selectors/profile';
import { actionResetDevtoolsUserProfileId, actionSetDevtoolsUserProfileId } from '@lib/core/users/slices/devtoolsUser';
import { actionResetUserProfile, actionSetUserProfileId } from '@lib/core/users/slices/profile';

axiosRetry(axios, { retries: 3 });

const controller = new AbortController();
export const getControllerSignal = () => controller.signal;
export const abortController = () => controller.abort();

export interface RequestArgs {
  url: string;
  body?: Record<string, any>;
  additionalHeaders?: Record<string, string>;
  ignoreProfileHeaders?: boolean;
  authToken?: string;
  method?: (typeof REQUEST_METHODS)[keyof typeof REQUEST_METHODS];
  params?: Record<never, string>;
  isDevtoolsRequest?: boolean;
}

export const request = async (args: RequestArgs): Promise<any | SerializedError> => {
  const {
    url,
    body = {},
    additionalHeaders,
    ignoreProfileHeaders,
    authToken,
    method = REQUEST_METHODS.GET,
    params,
    isDevtoolsRequest,
  } = args;
  const state = store.getState();
  const language = selectLocale(state);
  const profileId = isDevtoolsRequest ? selectDevtoolsUserProfileId(state) : selectProfileId(state);
  const requestAuthToken = authToken || isDevtoolsRequest ? selectDevtoolsUserAuthToken(state) : selectAuthToken(state);
  const headers: Record<string, string> = {
    'Accept-Language': language,
    'Content-Type': 'application/json',
    ...(profileId && !ignoreProfileHeaders && { 'x-profile': profileId }),
    ...(requestAuthToken && !url.includes('jgz') && { Authorization: `Bearer ${requestAuthToken}` }),
    ...additionalHeaders,
  };

  try {
    const response = await axios({
      baseURL: ENV_BACKEND_DOMAIN,
      headers,
      method,
      signal: getControllerSignal(),
      url,
      ...(method === REQUEST_METHODS.GET ? { params } : { data: JSON.stringify(body) }),
    });

    if (!ignoreProfileHeaders && profileId !== response.headers?.['x-profile']) {
      const profileHeader = response.headers?.['x-profile'];

      if (profileHeader) {
        store.dispatch(
          isDevtoolsRequest ? actionSetDevtoolsUserProfileId(profileHeader) : actionSetUserProfileId(profileHeader),
        );
      } else {
        store.dispatch(isDevtoolsRequest ? actionResetDevtoolsUserProfileId() : actionResetUserProfile());
      }
    }

    return Promise.resolve(response.data);
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAxiosErrorScenarios({
        error,
        headers,
        requestFunction: request,
        requestParams: args,
      });
    }
    return Promise.reject(parseError(error));
  }
};
